import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Package, Heart, LogOut, User, ShieldCheck, MapPin, Plus, Trash2, ReceiptText, ScanLine, Bell, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtPrice } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ProductGridSkeleton, ListSkeleton } from "@/components/Skeletons";
import QrScanner from "@/components/QrScanner";

const FUL_LABEL = { new: "جديد", preparing: "قيد التجهيز", ready: "جاهز للتسليم", delivering: "قيد التوصيل", delivered: "تم التسليم", cancelled: "ملغي" };
const PAY_LABEL = { pending: "بانتظار الدفع", paid: "مدفوع" };

export default function Account() {
  const { user, ready, logout } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "orders");
  const [orders, setOrders] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [showAddr, setShowAddr] = useState(false);
  const [addr, setAddr] = useState({ label: "المنزل", name: "", phone: "", country: "PS", city: "", address: "" });
  const navigate = useNavigate();

  useEffect(() => { if (ready && !user) navigate("/login?redirect=/account"); }, [ready, user]);
  const loadAddresses = () => api.get("/my/addresses").then((r) => setAddresses(r.data)).catch(() => {});
  useEffect(() => {
    if (!user) return;
    setLoadingData(true);
    Promise.allSettled([
      api.get("/my/orders").then((r) => setOrders(r.data)),
      api.get("/my/favorites").then((r) => setFavorites(r.data)),
      api.get("/my/addresses").then((r) => setAddresses(r.data)),
      api.get("/my/notifications").then((r) => setNotifications(r.data)),
    ]).finally(() => setLoadingData(false));
  }, [user]);

  useEffect(() => {
    if (tab === "notifications" && notifications.some((n) => !n.read)) {
      api.post("/my/notifications/read").then(() => setNotifications((ns) => ns.map((n) => ({ ...n, read: true })))).catch(() => {});
    }
  }, [tab, notifications.length]);

  const saveAddr = async () => {
    if (!addr.name || !addr.phone || !addr.city || !addr.address) { toast.error("أكمل الحقول"); return; }
    await api.post("/my/addresses", addr);
    toast.success("أُضيف العنوان");
    setShowAddr(false); setAddr({ label: "المنزل", name: "", phone: "", country: "PS", city: "", address: "" });
    loadAddresses();
  };
  const delAddr = async (id) => { await api.delete(`/my/addresses/${id}`); loadAddresses(); };

  const unread = notifications.filter((n) => !n.read).length;
  const isStaff = user && ["admin", "super_admin", "manager"].includes(user.role);

  if (!user) return (
    <div className="min-h-screen pt-24">
      <div className="border-b border-olive/10 bg-sand/30">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-6 py-10">
          <div className="h-14 w-14 animate-pulse rounded-full bg-olive/10" />
          <div className="space-y-2">
            <div className="h-5 w-40 animate-pulse rounded-full bg-olive/10" />
            <div className="h-3 w-56 animate-pulse rounded-full bg-olive/8" />
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-6xl px-6 py-8"><ListSkeleton count={3} /></div>
    </div>
  );

  return (
    <div className="min-h-screen pt-24">
      <div className="border-b border-olive/10 bg-sand/30">
        <div className="mx-auto max-w-6xl px-6 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-olive text-ivory"><User className="h-7 w-7" /></span>
              <div>
                <h1 className="font-display text-2xl font-bold text-ink">{user.name}</h1>
                <p className="text-sm text-ink/55">{user.email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              {isStaff && (
                <Link to="/admin" data-testid="go-admin" className="flex items-center gap-1.5 rounded-full bg-olive-dark px-4 py-2 text-sm font-medium text-ivory"><ShieldCheck className="h-4 w-4" /> لوحة الإدارة</Link>
              )}
              <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-1.5 rounded-full border border-olive/20 px-4 py-2 text-sm text-ink/70"><LogOut className="h-4 w-4" /> خروج</button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-6 flex gap-2 overflow-x-auto pb-1">
          <Tab active={tab === "orders"} onClick={() => setTab("orders")} icon={Package} testid="tab-orders">طلباتي ({orders.length})</Tab>
          <Tab active={tab === "invoices"} onClick={() => setTab("invoices")} icon={ReceiptText} testid="tab-invoices">فواتيري</Tab>
          <Tab active={tab === "scan"} onClick={() => setTab("scan")} icon={ScanLine} testid="tab-scan">مسح QR</Tab>
          <Tab active={tab === "notifications"} onClick={() => setTab("notifications")} icon={Bell} testid="tab-notifications">الإشعارات{unread > 0 ? ` (${unread})` : ""}</Tab>
          <Tab active={tab === "security"} onClick={() => setTab("security")} icon={Lock} testid="tab-security">الأمان</Tab>
          <Tab active={tab === "favorites"} onClick={() => setTab("favorites")} icon={Heart} testid="tab-favorites">المفضلة ({favorites.length})</Tab>
          <Tab active={tab === "addresses"} onClick={() => setTab("addresses")} icon={MapPin} testid="tab-addresses">العناوين ({addresses.length})</Tab>
        </div>

        {tab === "orders" && (
          loadingData ? <ListSkeleton count={3} /> :
          orders.length === 0 ? <Empty text="لا توجد طلبات بعد." /> : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.order_no} data-testid={`order-${o.order_no}`} className="rounded-2xl border border-olive/10 bg-white p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-display font-semibold text-ink">طلب {o.order_no}</div>
                    <span className="rounded-full bg-olive/10 px-3 py-1 text-xs font-medium text-olive">{FUL_LABEL[o.fulfillment_status] || "جديد"}</span>
                  </div>
                  <div className="mt-2 text-sm text-ink/55">{o.items.length} منتجات · الإجمالي {fmtPrice(o.total)}</div>
                  <div className="mt-2 text-xs text-ink/40">{new Date(o.created_at).toLocaleDateString("ar")}</div>
                </div>
              ))}
            </div>
          )
        )}

        {tab === "invoices" && (
          loadingData ? <ListSkeleton count={3} /> :
          orders.length === 0 ? <Empty text="لا توجد فواتير بعد." /> : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.order_no} data-testid={`invoice-row-${o.order_no}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-olive/10 bg-white p-4">
                  <div>
                    <div className="font-display font-semibold text-ink">فاتورة {o.order_no}</div>
                    <div className="mt-1 text-xs text-ink/50">{new Date(o.created_at).toLocaleDateString("ar")} · {fmtPrice(o.total)} · {PAY_LABEL[o.status] || o.status}</div>
                  </div>
                  {o.qr_token ? (
                    <Link to={`/invoice/${o.qr_token}`} data-testid={`invoice-open-${o.order_no}`}
                      className="flex items-center gap-1.5 rounded-full bg-olive px-5 py-2.5 text-sm font-semibold text-ivory transition-colors hover:bg-olive-dark">
                      <ReceiptText className="h-4 w-4" /> عرض الفاتورة
                    </Link>
                  ) : (
                    <span className="text-xs text-ink/40">الفاتورة تتاح بعد التحقق من الطرد</span>
                  )}
                </div>
              ))}
            </div>
          )
        )}

        {tab === "scan" && <QrScanner />}

        {tab === "security" && <SecurityTab />}

        {tab === "notifications" && (
          loadingData ? <ListSkeleton count={2} /> :
          notifications.length === 0 ? <Empty text="لا توجد إشعارات." /> : (
            <div className="space-y-2">
              {notifications.map((n) => (
                <div key={n.id} data-testid={`notification-${n.id}`} className={`rounded-2xl border px-5 py-3.5 text-sm ${n.read ? "border-olive/10 bg-white text-ink/60" : "border-olive/25 bg-olive/[0.05] text-ink"}`}>
                  <div className="flex items-center justify-between">
                    <span>{n.text}</span>
                    <span className="text-xs text-ink/40">{new Date(n.created_at).toLocaleString("ar")}</span>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {tab === "favorites" && (
          loadingData ? <ProductGridSkeleton count={4} gridClass="grid grid-cols-2 gap-4 md:grid-cols-4 lg:gap-6" /> :
          favorites.length === 0 ? <Empty text="لا توجد منتجات مفضلة بعد." /> : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:gap-6">
              {favorites.map((p, i) => <ProductCard key={p.sku} product={p} index={i} />)}
            </div>
          )
        )}

        {tab === "addresses" && (
          <div>
            <div className="mb-4 flex justify-end">
              {!showAddr && <button data-testid="add-address" onClick={() => setShowAddr(true)} className="flex items-center gap-1.5 rounded-full bg-olive px-4 py-2 text-sm font-medium text-ivory"><Plus className="h-4 w-4" /> عنوان جديد</button>}
            </div>
            {showAddr && (
              <div className="mb-5 rounded-2xl border border-olive/10 bg-white p-5">
                <div className="grid gap-3 sm:grid-cols-2">
                  <AInput label="التسمية" value={addr.label} onChange={(v) => setAddr({ ...addr, label: v })} />
                  <AInput label="الاسم" testid="addr-name" value={addr.name} onChange={(v) => setAddr({ ...addr, name: v })} />
                  <AInput label="الهاتف" testid="addr-phone" value={addr.phone} onChange={(v) => setAddr({ ...addr, phone: v })} />
                  <AInput label="المدينة" testid="addr-city" value={addr.city} onChange={(v) => setAddr({ ...addr, city: v })} />
                  <div className="sm:col-span-2"><AInput label="العنوان التفصيلي" testid="addr-address" value={addr.address} onChange={(v) => setAddr({ ...addr, address: v })} /></div>
                </div>
                <div className="mt-4 flex gap-2">
                  <button data-testid="save-address" onClick={saveAddr} className="rounded-full bg-olive px-6 py-2.5 font-semibold text-ivory">حفظ</button>
                  <button onClick={() => setShowAddr(false)} className="rounded-full border border-olive/20 px-6 py-2.5 text-ink/60">إلغاء</button>
                </div>
              </div>
            )}
            {loadingData ? <ListSkeleton count={2} /> :
            addresses.length === 0 && !showAddr ? <Empty text="لا توجد عناوين محفوظة." /> : (
              <div className="grid gap-3 sm:grid-cols-2">
                {addresses.map((a) => (
                  <div key={a.id} data-testid={`address-${a.id}`} className="rounded-2xl border border-olive/10 bg-white p-4">
                    <div className="flex items-center justify-between">
                      <span className="rounded-full bg-olive/10 px-3 py-1 text-xs font-medium text-olive">{a.label}</span>
                      <button onClick={() => delAddr(a.id)} className="text-ink/40 hover:text-palred"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <div className="mt-2 font-medium text-ink">{a.name} · {a.phone}</div>
                    <div className="text-sm text-ink/55">{a.city} — {a.address}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const Tab = ({ active, onClick, icon: Icon, children, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${active ? "bg-olive text-ivory" : "bg-white text-ink/60 border border-olive/15"}`}>
    <Icon className="h-4 w-4" /> {children}
  </button>
);

const Empty = ({ text }) => (
  <div className="rounded-3xl border border-dashed border-olive/20 py-20 text-center text-ink/50">{text} <Link to="/store" className="text-olive">تصفّح المتجر</Link></div>
);

function SecurityTab() {
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.current || !f.next) { toast.error("أكمل الحقول"); return; }
    if (f.next.length < 8) { toast.error("كلمة المرور الجديدة يجب ألا تقل عن 8 أحرف"); return; }
    if (f.next !== f.confirm) { toast.error("كلمتا المرور الجديدتان غير متطابقتين"); return; }
    setBusy(true);
    try {
      await api.post("/my/password", { current_password: f.current, new_password: f.next });
      toast.success("تم تغيير كلمة المرور بنجاح");
      setF({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "تعذّر تغيير كلمة المرور");
    }
    setBusy(false);
  };

  return (
    <div data-testid="security-tab" className="max-w-lg rounded-3xl border border-olive/10 bg-white p-6">
      <h3 className="mb-1 flex items-center gap-2 font-display text-lg font-bold text-ink"><Lock className="h-5 w-5 text-olive" /> تغيير كلمة المرور</h3>
      <p className="mb-5 text-sm text-ink/55">أدخل كلمة مرورك الحالية لتأكيد هويتك، ثم الجديدة مرتين.</p>
      <div className="space-y-3">
        <AInput label="كلمة المرور الحالية" testid="pw-current" type="password" value={f.current} onChange={(v) => setF({ ...f, current: v })} />
        <AInput label="كلمة المرور الجديدة (8 أحرف فأكثر)" testid="pw-new" type="password" value={f.next} onChange={(v) => setF({ ...f, next: v })} />
        <AInput label="تأكيد كلمة المرور الجديدة" testid="pw-confirm" type="password" value={f.confirm} onChange={(v) => setF({ ...f, confirm: v })} />
      </div>
      <button data-testid="pw-save" onClick={save} disabled={busy}
        className="mt-5 rounded-full bg-olive px-8 py-3 font-semibold text-ivory transition-colors hover:bg-olive-dark disabled:opacity-60">
        {busy ? "جارِ الحفظ..." : "حفظ كلمة المرور"}
      </button>
    </div>
  );
}

const AInput = ({ label, value, onChange, testid, type = "text" }) => (
  <div>
    <label className="mb-1.5 block text-sm text-ink/60">{label}</label>
    <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-olive/20 px-4 py-2.5 outline-none focus:border-olive" />
  </div>
);
